import { DocumentTypeInfo } from '../types/document-type';

/**
 * Resolves a translation key to its text. The platform translation service
 * echoes the key back when nothing is registered — that is how the fallback
 * to the backend's own wording is detected.
 */
export type TranslateFn = (key: string) => string;

type PartialDocumentType = Partial<DocumentTypeInfo> | null | undefined;

/**
 * Contract with the document-query-service: `displayKey` is the BASE key of a
 * document type (e.g. `#DOCUMENT_TYPE.OFFER`); the frontend appends the slot.
 * A customer overrides a wording by shipping the key in its own translation
 * file — without a key (or without an override) the backend text is used.
 */
function fromKey(
  displayKey: string | null | undefined,
  slot: string,
  translate: TranslateFn
): string | null {
  if (!displayKey) return null;
  const key = `${displayKey}.${slot}`;
  const value = translate(key);
  return value && value !== key ? value : null;
}

export function documentTypeName(
  type: PartialDocumentType,
  translate: TranslateFn
): string {
  return (
    fromKey(type?.displayKey, 'NAME', translate) ??
    type?.displayName ??
    type?.type ??
    ''
  );
}

export function documentTypeNamePlural(
  type: PartialDocumentType,
  translate: TranslateFn
): string {
  return (
    fromKey(type?.displayKey, 'NAME_PLURAL', translate) ??
    type?.displayNamePlural ??
    documentTypeName(type, translate)
  );
}

export function documentTypeAbbreviation(
  type: PartialDocumentType,
  translate: TranslateFn
): string {
  return (
    fromKey(type?.displayKey, 'ABBREVIATION', translate) ?? type?.abbreviation ?? ''
  );
}
